import React, { useState, useEffect, useMemo } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, InputAdornment, Button, Typography } from '@mui/material';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import useCachedCollection from '../hooks/useCachedCollection';
import SearchIcon from '@mui/icons-material/Search';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices'; // SGK Icon substitute
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { startOfDay, endOfDay, subMonths } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

import GlassCard from '../ui/glass/components/GlassCard';
import PageHeader from '../components/common/PageHeader';
import StatusChip from '../components/common/StatusChip';
import EmptyState from '../components/common/EmptyState';
import { Loading } from '../components/common/Loading';

// Transfer logları için oturum-içi cache.
//
// Bu sayfa her açılışında `device_logs` içindeki TÜM transfer kayıtlarını çekiyordu
// (ölçüm: 869 doküman). Kayıtlar geçmişe ait ve nadiren değişir, bu yüzden sonuç
// modül seviyesinde tutulur; aynı oturumda sayfaya tekrar girmek okuma yapmaz.
let transferLoglariCache = null; // { logsByDevice, ts }
const TRANSFER_TTL_MS = 5 * 60 * 1000;

async function transferLoglariniGetir() {
    if (transferLoglariCache && Date.now() - transferLoglariCache.ts < TRANSFER_TTL_MS) {
        return transferLoglariCache.logsByDevice;
    }
    const q = query(collection(db, 'device_logs'), where('actionType', '==', 'transfer'));
    const snapshot = await getDocs(q);

    const logsByDevice = {};
    snapshot.docs.forEach(doc => {
        const data = doc.data();
        if (!logsByDevice[data.deviceId]) logsByDevice[data.deviceId] = [];
        logsByDevice[data.deviceId].push({ ...data, id: doc.id });
    });
    // Her cihazın logları en yeniden eskiye sıralı olsun (tüketiciler bunu varsayıyor).
    Object.values(logsByDevice).forEach(logs =>
        logs.sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0))
    );

    transferLoglariCache = { logsByDevice, ts: Date.now() };
    return logsByDevice;
}

const SgkCihazListesi = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState(subMonths(new Date(), 1));
    const [endDate, setEndDate] = useState(new Date());
    const [visibleCount, setVisibleCount] = useState(25); // ekranda gösterilen satır sayısı (DOM'u hafifletir)

    // Bu sayfa salt-okunur. Eskiden `where('isSgk','==',true)` ile kalıcı realtime
    // listener açıyordu (~616 doküman, her açılışta). Artık cihazlar paylaşımlı
    // oturum cache'inden gelir ve SGK filtresi client tarafında uygulanır: Cihazlar,
    // Bakım ve bu sayfa aynı cache'i paylaştığı için oturumda tek okuma yeterli olur.
    const { data: allDevices, loading } = useCachedCollection('devices');

    const devices = useMemo(() => {
        const zaman = (d) => {
            if (!d) return 0;
            if (d.toDate) return d.toDate().getTime(); // Firestore Timestamp
            return new Date(d).getTime();              // String/Date
        };
        return allDevices
            .filter(d => d.isSgk === true)
            .sort((a, b) => zaman(b.zimmetTarihi) - zaman(a.zimmetTarihi));
    }, [allDevices]);

    // Memoize edilir; her tuş/aksiyon sonrası ~600 cihaz yeniden filtrelenip tüm tablo
    // yeniden render edilmesin diye (donma/kasma nedeni).
    const filteredDevices = useMemo(() => {
        const searchLower = searchTerm.toLowerCase();
        return devices.filter(device => {
            const matchesSearch =
                (device.musteriAdSoyad || '').toLowerCase().includes(searchLower) ||
                (device.seriNumarasi || '').toLowerCase().includes(searchLower) ||
                (device.marka || '').toLowerCase().includes(searchLower) ||
                (device.model || '').toLowerCase().includes(searchLower);

            let matchesDate = true;
            if (device.zimmetTarihi) {
                const deviceDate = device.zimmetTarihi.toDate ? device.zimmetTarihi.toDate() : new Date(device.zimmetTarihi);
                if (startDate && deviceDate < startOfDay(startDate)) matchesDate = false;
                if (endDate && deviceDate > endOfDay(endDate)) matchesDate = false;
            } else {
                // Tarihi olmayan cihazlar da gösterilecek (v6 güncellemesi)
                matchesDate = true;
            }

            return matchesSearch && matchesDate;
        });
    }, [devices, searchTerm, startDate, endDate]);

    // Ekranda yalnızca ilk `visibleCount` cihazı göster; arama/filtre tüm veri üzerinde çalışır.
    const visibleDevices = filteredDevices.slice(0, visibleCount);

    // Güvenli tarih formatlama
    const formatDate = (dateValue) => {
        if (!dateValue) return '-';
        try {
            const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
            return format(date, 'dd MMM yyyy', { locale: tr });
        } catch (error) {
            console.error('Tarih formatlama hatası:', error);
            return '-';
        }
    };

    const [transferInfo, setTransferInfo] = useState({});

    // Cihazlar yüklendiğinde transfer geçmişlerini çek
    useEffect(() => {
        if (devices.length === 0) return;

        const fetchTransferLogs = async () => {
            // Transfer logları oturum cache'inden gelir (bkz. transferLoglariniGetir).
            // Firestore 'in' sorgusu en fazla 10 eleman aldığı için cihaz bazlı sorgu
            // yapılamıyor; tüm transfer logları bir kez çekilip client'ta gruplanıyor.
            try {
                const logsByDevice = await transferLoglariniGetir();

                const infoMap = {};

                // Her cihaz için son transferi bul (loglar zaten en yeniden eskiye sıralı)
                devices.forEach(device => {
                    const logs = logsByDevice[device.id];
                    if (logs && logs.length > 0) {

                        // Şu anki sahibine kim devretmiş?
                        const lastTransferToCurrentOwner = logs.find(l => l.metadata?.toCustomerId === device.musteriId);

                        let devreden = '-';
                        let devirTarihi = null;

                        if (lastTransferToCurrentOwner) {
                            devreden = lastTransferToCurrentOwner.metadata?.fromCustomerName || '-';
                            devirTarihi = lastTransferToCurrentOwner.timestamp;
                        } else {
                            // En son transfer
                            const lastLog = logs[0];
                            devreden = `${lastLog.metadata?.fromCustomerName}`; // Sadece devredeni gösterelim, tablo karışmasın
                            devirTarihi = lastLog.timestamp;
                        }

                        infoMap[device.id] = { devreden, devirTarihi };
                    }
                });

                setTransferInfo(infoMap);
            } catch (err) {
                console.error("Transfer logları çekilirken hata:", err);
            }
        };

        fetchTransferLogs();
    }, [devices]);

    const [exporting, setExporting] = useState(false);

    const handleExport = async () => {
        setExporting(true);
        try {
            const dataToExport = filteredDevices.map(device => {
                const info = transferInfo[device.id] || {};
                return {
                    'Müşteri Adı Soyadı': device.musteriAdSoyad || '-',
                    'Marka': device.marka || '-',
                    'Model': device.model || '-',
                    'Seri Numarası': device.seriNumarasi || '-',
                    'Cihaz Sınıfı': device.cihazSinifi || '-',
                    'Zimmet Tarihi': formatDate(device.zimmetTarihi),
                    'Devreden Kişi/Kurum': info.devreden || '-',
                    'Devir Tarihi': formatDate(info.devirTarihi),
                    'Durum': 'Zimmetli'
                };
            });

            const worksheet = XLSX.utils.json_to_sheet(dataToExport);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "SGK Zimmetli Cihazlar");
            XLSX.writeFile(workbook, `SGK_Zimmetli_Cihazlar_${format(new Date(), 'dd-MM-yyyy')}.xlsx`);
        } catch (error) {
            console.error("Export error:", error);
            alert("Excel oluşturulurken bir hata meydana geldi.");
        } finally {
            setExporting(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 1, md: 1 } }}>
            <PageHeader
                title="SGK Zimmetli Cihazlar"
                subtitle="Resmi zimmetli cihazların takibini buradan yapabilirsiniz."
                icon={<MedicalServicesIcon />}
            >
                        <DatePicker
                            label="Başlangıç Tarihi"
                            value={startDate}
                            onChange={(newValue) => setStartDate(newValue)}
                            slotProps={{ textField: { size: 'small', sx: { width: 140, bgcolor: 'background.paper', borderRadius: 1 } } }}
                        />
                        <DatePicker
                            label="Bitiş Tarihi"
                            value={endDate}
                            onChange={(newValue) => setEndDate(newValue)}
                            slotProps={{ textField: { size: 'small', sx: { width: 140, bgcolor: 'background.paper', borderRadius: 1 } } }}
                        />
                        <Button
                            variant="contained"
                            color="success"
                            onClick={handleExport}
                            disabled={filteredDevices.length === 0 || exporting}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            {exporting ? 'Hazırlanıyor...' : "Excel'e Aktar"}
                        </Button>
                        <TextField
                            size="small"
                            placeholder="Ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>
                            }}
                            sx={{ width: 150, bgcolor: 'background.paper', borderRadius: 1 }}
                        />
            </PageHeader>

            <GlassCard sx={{ width: '100%', overflow: 'hidden', p: 0 }}>
                <TableContainer>
                    {loading ? (
                        <Loading message="SGK cihazları yükleniyor..." />
                    ) : filteredDevices.length === 0 ? (
                        <EmptyState
                            icon={<MedicalServicesIcon />}
                            message="Kayıtlı SGK cihazı bulunamadı."
                            description={searchTerm ? 'Aramanızla eşleşen cihaz yok.' : undefined}
                        />
                    ) : (
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Müşteri</TableCell>
                                    <TableCell>Marka / Model</TableCell>
                                    <TableCell>Seri Numarası</TableCell>
                                    <TableCell>Sınıf</TableCell>
                                    <TableCell>Zimmet Tarihi</TableCell>
                                    <TableCell>Devreden</TableCell>
                                    <TableCell>Devir Tarihi</TableCell>
                                    <TableCell>Durum</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleDevices.map(device => {
                                    const info = transferInfo[device.id] || {};
                                    return (
                                        <TableRow
                                            key={device.id}
                                            hover
                                            onClick={() => navigate(`/cihaz/${device.id}`)}
                                            sx={{
                                                cursor: 'pointer',
                                                bgcolor: (device.isHurda || device.cihazHurdada) ? 'rgba(211, 47, 47, 0.04)' : 'inherit'
                                            }}
                                        >
                                            <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>{device.musteriAdSoyad || '-'}</TableCell>
                                            <TableCell>{device.marka} {device.model}</TableCell>
                                            <TableCell sx={{ fontFamily: 'monospace' }}>
                                                {device.seriNumarasi}
                                                {(device.isHurda || device.cihazHurdada) && <StatusChip label="HURDA" color="error" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />}
                                            </TableCell>
                                            <TableCell>{device.cihazSinifi || '-'}</TableCell>
                                            <TableCell>
                                                {formatDate(device.zimmetTarihi)}
                                            </TableCell>
                                            <TableCell sx={{ color: info.devreden ? 'primary.main' : 'inherit' }}>
                                                {info.devreden || '-'}
                                            </TableCell>
                                            <TableCell>
                                                {formatDate(info.devirTarihi)}
                                            </TableCell>
                                            <TableCell>
                                                {(device.isHurda || device.cihazHurdada) ? (
                                                    <StatusChip label="Hurda" color="error" />
                                                ) : (
                                                    <StatusChip label="Zimmetli" color="warning" />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </TableContainer>
            </GlassCard>

            {visibleCount < filteredDevices.length && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">{visibleDevices.length} / {filteredDevices.length} gösteriliyor</Typography>
                    <Button variant="outlined" onClick={() => setVisibleCount(c => c + 25)}>Daha Fazla Yükle</Button>
                </Box>
            )}
        </Box>
    );
};

export default SgkCihazListesi;
